import { jsx as _jsx } from "react/jsx-runtime";
/**
 * PasscodeInput Component
 *
 * Reusable 6-digit passcode input component for login/verification flows.
 * Mimics the legacy jQuery `user_login_token_text_passcode` behavior.
 *
 * Features:
 * - 6 separate input fields
 * - Auto-focus next on input
 * - Backspace moves to previous
 * - Arrow key navigation
 * - Paste support (full code)
 * - Optional prefill (dev mode)
 */
import React, { useRef, useEffect, useCallback } from 'react';
export function PasscodeInput({ length = 6, value = '', onChange, onComplete, onPasteComplete, disabled = false, hasError = false, autoFocus = true, className = '', inputClassName = '', }) {
    const inputRefs = useRef([]);
    // [CU-869eq7fke] az utoljára beküldött teljes kód — ugyanazt az értéket a
    // kitöltöttség-figyelő effekt NEM küldi be újra (a szülő újrarenderelése,
    // pl. hiba-állapot vagy nem memoizált onComplete miatt korábban végtelen
    // beküldési ciklust okozott a 2FA-képernyőn)
    const lastCompletedRef = useRef('');
    // Convert value string to array of digits.
    // FIGYELEM: padEnd() ÜRES kitöltő-karakterrel NEM tölt ki semmit
    // (''.padEnd(6, '') === ''), így üres értéknél nulla beviteli mező
    // renderelődött — a kód-bekérő lépés használhatatlan volt. Fix hosszú
    // tömböt építünk, a hiányzó helyeket üres stringgel töltve.
    const digits = Array.from({ length }, (_, i) => value[i] || '');
    // Focus first input on mount
    useEffect(() => {
        if (autoFocus && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [autoFocus]);
    // Check completion — csak az ÚJ teljes értékre fut, ismételt renderre nem
    useEffect(() => {
        if (value.length === length) {
            if (value !== lastCompletedRef.current) {
                lastCompletedRef.current = value;
                if (onComplete) {
                    onComplete(value);
                }
            }
        }
        else {
            lastCompletedRef.current = '';
        }
    }, [value, length, onComplete]);
    const updateValue = useCallback((newDigits) => {
        const newValue = newDigits.join('');
        if (onChange) {
            onChange(newValue);
        }
    }, [onChange]);
    const handleChange = (index, inputValue) => {
        // Only allow single digit
        const digit = inputValue.replace(/\D/g, '').slice(-1);
        const newDigits = [...digits];
        newDigits[index] = digit;
        updateValue(newDigits);
        // Auto-focus next input if digit entered
        if (digit && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };
    const handleKeyDown = (index, e) => {
        switch (e.key) {
            case 'Backspace':
                // If current is empty, move to previous and clear it
                if (!digits[index] && index > 0) {
                    const newDigits = [...digits];
                    newDigits[index - 1] = '';
                    updateValue(newDigits);
                    inputRefs.current[index - 1]?.focus();
                }
                break;
            case 'ArrowLeft':
                if (index > 0) {
                    inputRefs.current[index - 1]?.focus();
                    e.preventDefault();
                }
                break;
            case 'ArrowRight':
                if (index < length - 1) {
                    inputRefs.current[index + 1]?.focus();
                    e.preventDefault();
                }
                break;
            case 'Enter':
                // Submit on Enter if complete
                if (value.length === length && onComplete) {
                    onComplete(value);
                    e.preventDefault();
                }
                break;
        }
    };
    const handlePaste = (e) => {
        e.preventDefault();
        if (disabled) {
            return;
        }
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
        if (pasted.length > 0) {
            const newDigits = pasted.padEnd(length, '').slice(0, length).split('');
            // Only fill with pasted digits, leave rest empty
            for (let i = pasted.length; i < length; i++) {
                newDigits[i] = '';
            }
            updateValue(newDigits);
            // Focus the next empty or last filled
            const focusIndex = Math.min(pasted.length, length - 1);
            inputRefs.current[focusIndex]?.focus();
            // Teljes kód egy beillesztéssel: a hívó azonnal beküldheti. A
            // kitöltöttség-figyelő effekt ugyanerre az értékre már nem hív
            // onComplete-et (lastCompletedRef), így nincs dupla beküldés ott, ahol
            // mindkét callback ugyanazt csinálná.
            if (pasted.length === length && onPasteComplete) {
                lastCompletedRef.current = pasted;
                onPasteComplete(pasted);
            }
        }
    };
    const handleFocus = (e) => {
        // Select content on focus for easier replacement
        e.target.select();
    };
    const containerClass = `passcode-input ${hasError ? 'passcode-input--error' : ''} ${className}`.trim();
    return (_jsx("div", { className: containerClass, onPaste: handlePaste, children: digits.map((digit, index) => (_jsx("input", { ref: (el) => (inputRefs.current[index] = el), type: "text", inputMode: "numeric", pattern: "[0-9]*", className: `passcode-input__digit ${inputClassName}`.trim(), value: digit, onChange: (e) => handleChange(index, e.target.value), onKeyDown: (e) => handleKeyDown(index, e), onFocus: handleFocus, maxLength: 2, disabled: disabled, autoComplete: "one-time-code", "aria-label": `Digit ${index + 1} of ${length}` }, index))) }));
}
export default PasscodeInput;
